// Copyright (c) 2024-present, Dinocollab Technologies, Inc. and its affiliates. All rights reserved.

// imports
import { useEffect, useMemo, useRef, useState } from 'react'
import { CombinedTransition, DEFAULT_ANIMATION, ViewDefault } from './view-switch-transition.units'
import { viewSwitchTransitionClasses, ViewSwitchTransitionStyled } from './view-switch-transition.units'
// types
import type { FC, ReactNode, ComponentType } from 'react'
import type { TAnimationConfig } from './view-switch-transition.units'

type TViewSwitchTransitionConfig<T extends string = string> = {
  value: T
  label?: string
  Content: ComponentType<{ value: T }>
  icon?: ReactNode
}

type TViewSwitchTransitionParam<T extends string = string> = {
  viewA?: TViewSwitchTransitionConfig<T>
  viewB?: TViewSwitchTransitionConfig<T>
  transitionConfig?: TAnimationConfig
}

interface IViewSwitchTransitionProps<T extends string = string> {
  value?: T
  viewA?: TViewSwitchTransitionConfig<T>
  viewB?: TViewSwitchTransitionConfig<T>
  transitionConfig?: TAnimationConfig
}

export function createViewSwitchTransition<T extends string = string>(config?: TViewSwitchTransitionParam<T>) {
  const classes = viewSwitchTransitionClasses
  const viewAClass = `${classes.view} ${classes.viewA}`
  const viewBClass = `${classes.view} ${classes.viewB}`

  const ViewSwitchTransition: FC<IViewSwitchTransitionProps<T>> = (props) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null)
    const viewARef = useRef<HTMLDivElement | null>(null)
    const viewBRef = useRef<HTMLDivElement | null>(null)

    const [mountedMode, setMountedMode] = useState<T | undefined>(props.value ?? config?.viewA?.value)
    const [mountedOpen, setMountedOpen] = useState<boolean>(true)

    const pendingModeRef = useRef<T | null>(null)
    const exitTimerRef = useRef<number | null>(null)

    const finalConfig = useMemo(() => {
      return {
        viewA: props.viewA ?? config?.viewA,
        viewB: props.viewB ?? config?.viewB
      }
    }, [props.viewA, props.viewB, config?.viewA, config?.viewB])

    const animations = useMemo(() => {
      return Object.assign({}, DEFAULT_ANIMATION, config?.transitionConfig, props.transitionConfig ?? {})
    }, [config?.transitionConfig, props.transitionConfig])

    useEffect(() => {
      if (props.value === mountedMode) return

      if (exitTimerRef.current) {
        window.clearTimeout(exitTimerRef.current)
        exitTimerRef.current = null
      }

      pendingModeRef.current = props.value ?? null

      const currentHeight = wrapperRef.current?.offsetHeight
      if (wrapperRef.current && typeof currentHeight === 'number') {
        wrapperRef.current.style.height = `${currentHeight}px`
        wrapperRef.current.style.transition = `height ${animations.enter}ms ${animations.ease}`
      }

      setMountedOpen(false)

      exitTimerRef.current = window.setTimeout(() => {
        const nextMode = pendingModeRef.current
        pendingModeRef.current = null

        if (nextMode) {
          setMountedMode(nextMode)
          requestAnimationFrame(() => {
            const targetRef = nextMode === finalConfig.viewA?.value ? viewARef : viewBRef
            const nextHeight = targetRef.current?.offsetHeight ?? wrapperRef.current?.offsetHeight ?? 0
            setMountedOpen(true)

            if (wrapperRef.current) {
              wrapperRef.current.style.height = `${nextHeight}px`
              window.setTimeout(() => {
                if (wrapperRef.current) wrapperRef.current.style.height = ''
              }, animations.enter)
            }
          })
        }

        exitTimerRef.current = null
      }, animations.exit + animations.hold)

      return () => {
        if (exitTimerRef.current) {
          window.clearTimeout(exitTimerRef.current)
          exitTimerRef.current = null
        }
      }
    }, [animations, props.value, mountedMode])

    const ViewAContent = useMemo(() => {
      return finalConfig.viewA?.Content ?? ViewDefault
    }, [finalConfig.viewA?.Content])

    const ViewBContent = useMemo(() => {
      return finalConfig.viewB?.Content ?? ViewDefault
    }, [finalConfig.viewB?.Content])

    return (
      <ViewSwitchTransitionStyled className={classes.root}>
        <div ref={wrapperRef} className={classes.viewWrapper}>
          {mountedMode === finalConfig.viewA?.value && (
            <CombinedTransition
              open={mountedOpen}
              timeout={{ enter: animations.enter, exit: animations.exit }}
              mountDelay={animations.mountDelay}
              animationConfig={animations}
              style={{ transformOrigin: animations.transformOriginGrid }}
            >
              <div ref={viewARef} className={viewAClass}>
                <ViewAContent value={finalConfig.viewA?.value!} />
              </div>
            </CombinedTransition>
          )}

          {mountedMode === finalConfig.viewB?.value && (
            <CombinedTransition
              open={mountedOpen}
              timeout={{ enter: animations.enter, exit: animations.exit }}
              mountDelay={animations.mountDelay}
              animationConfig={animations}
              style={{ transformOrigin: animations.transformOriginTable }}
            >
              <div ref={viewBRef} className={viewBClass}>
                <ViewBContent value={finalConfig.viewB?.value!} />
              </div>
            </CombinedTransition>
          )}
        </div>
      </ViewSwitchTransitionStyled>
    )
  }

  return ViewSwitchTransition
}

export const ViewSwitchTransition = createViewSwitchTransition({})

export default ViewSwitchTransition
